import React, { Component } from 'react'
import gql from "graphql-tag";
import { withApollo } from "react-apollo";
import * as Cookies from "es-cookie";
import { TableShimmer } from '../preloader/PageCardShimmer';
import { canEdit } from "../../../utils/modulePermissions";

// Rows in roleModuleAccess match on module_key OR module_name — pass both spellings.
const EMPLOYEE_RESTRICTIONS_MODULE = ["Employee_Restrctions", "staff"];
// This is the Module Access tab. module_key "Employee_Restrctions" has separate rows per
// sub_module_name ("employees" / "roles" / "module-access"); scope to "module-access" so
// this tab reads only its own grant and the other tabs' grants can't leak in.
const EMPLOYEE_RESTRICTIONS_SUB = "module-access";

// NOTE: assumed backend operations — confirm/adjust names + fields with your API.
const GET_EMPLOYEE_ROLES = gql`
  query($institute_id: Int) {
    getEmployeeRoles(institute_id: $institute_id) {
      id
      role_name
      is_mandatory
    }
  }
`;

const GET_ROLE_MODULE_ACCESS = gql`
  query($institute_id: Int, $employee_role_id: Int) {
    getRoleModuleAccess(institute_id: $institute_id, employee_role_id: $employee_role_id) {
      module_id
      module_name
      display_name
      sub_module_name
      is_view
      is_edit
      is_delete
    }
  }
`;

const SAVE_ROLE_MODULE_ACCESS = gql`
  mutation($params: [RoleModuleAccessInput]) {
    saveRoleModuleAccess(params: $params)
  }
`;

const PERMS = [
    { key: "is_view", label: "View" },
    { key: "is_edit", label: "Edit" },
    { key: "is_delete", label: "Delete" },
];

class ModuleAccessTab extends Component {
    constructor(props) {
        super(props);
        this.state = {
            roles: [],
            selectedRole: "",
            rows: [],          // flat: [{ key, module_id, module_name, sub_module_name }]
            access: {},        // { [rowKey]: { is_view, is_edit, is_delete } }  rowKey = `${module_id}__${sub_module_name}`
            search: "",
            loadingAccess: false,
            saving: false,
            savedMsg: "",
            errorMsg: "",
            showSuccessPopup: false,
        };
    }

    componentWillUnmount() {
        if (this._popupTimer) clearTimeout(this._popupTimer);
    }

    componentDidMount() {
        this.getRoles();
    }

    getRows = () => this.state.rows || [];

    // Rows left after the search box — drives the grid and the column "All"
    // toggles. Save always uses getRows(), so filtering never drops a module's
    // permissions just because it's hidden.
    getVisibleRows = () => {
        const q = this.state.search.trim().toLowerCase();
        if (!q) return this.getRows();
        return this.getRows().filter((r) =>
            [r.display_name, r.module_name, r.sub_module_name]
                .some((v) => (v || "").toLowerCase().includes(q))
        );
    };

    getBranches = () => this.props.getBranches || [];

    // a mandatory role's access is fixed — its checkboxes are read-only
    isMandatoryRole = () => {
        const role = this.state.roles.find((r) => String(r.id) === String(this.state.selectedRole));
        return !!(role && role.is_mandatory);
    };

    rowKey = (r) => `${r.module_id}__${r.sub_module_name || ""}`;

    // Column "All" applies to what's on screen, so it follows the search.
    allRowKeys = () => this.getVisibleRows().map((r) => r.key);

    getRoles = async () => {
        try {
            const result = await this.props.client.query({
                query: GET_EMPLOYEE_ROLES,
                variables: { institute_id: parseInt(Cookies.get("institutionid")) },
                fetchPolicy: "network-only",
            });
            const roles = (result.data && result.data.getEmployeeRoles) || [];
            // Land on the first role instead of the "Select a role" empty state — the
            // grid is the point of this tab, so show it filled in straight away. The
            // dropdown still lets you switch (or clear back to the placeholder).
            const first = roles[0];
            this.setState({ roles, selectedRole: first ? String(first.id) : "" });
            if (first) this.loadAccess(first.id);
        } catch {
            this.setState({ roles: [] });
        }
    };

    handleRoleChange = async (e) => {
        const employee_role_id = e.target.value;
        this.setState({ selectedRole: employee_role_id, savedMsg: "", errorMsg: "" });

        // module access is role-wide — fetch as soon as a role is chosen
        if (!employee_role_id) {
            this.setState({ rows: [], access: {} });
            return;
        }
        await this.loadAccess(employee_role_id);
    };

    loadAccess = async (employee_role_id) => {
        this.setState({ loadingAccess: true });
        const rows = [];
        const map = {};
        try {
            const result = await this.props.client.query({
                query: GET_ROLE_MODULE_ACCESS,
                variables: {
                    institute_id: parseInt(Cookies.get("institutionid")),
                    employee_role_id: parseInt(employee_role_id),
                },
                fetchPolicy: "network-only",
            });
            const data = (result.data && result.data.getRoleModuleAccess) || [];
            // flat (module, sub_module) rows straight from the response
            data.forEach((r) => {
                const key = this.rowKey(r);
                rows.push({
                    key,
                    module_id: r.module_id,
                    module_name: r.module_name,
                    display_name: r.display_name || r.module_name,
                    sub_module_name: r.sub_module_name || "",
                });
                map[key] = {
                    is_view: r.is_view ? 1 : 0,
                    is_edit: r.is_edit ? 1 : 0,
                    is_delete: r.is_delete ? 1 : 0,
                };
            });
        } catch {
            // no access returned — show an empty grid
        }
        this.setState({ rows, access: map, loadingAccess: false });
    };

    toggle = (rowKey, key) => {
        this.setState((prev) => {
            const current = prev.access[rowKey] || { is_view: 0, is_edit: 0, is_delete: 0 };
            const next = { ...current, [key]: current[key] ? 0 : 1 };
            // View is a prerequisite for Edit/Delete: turning either on auto-selects View...
            if ((key === "is_edit" || key === "is_delete") && next[key]) {
                next.is_view = 1;
            }
            // ...and turning View off clears Edit/Delete (can't edit/delete without view).
            if (key === "is_view" && !next.is_view) {
                next.is_edit = 0;
                next.is_delete = 0;
            }
            return {
                access: { ...prev.access, [rowKey]: next },
                savedMsg: "",
            };
        });
    };

    columnAllChecked = (key) => {
        const keys = this.allRowKeys();
        return keys.length > 0 && keys.every((rk) => this.state.access[rk] && this.state.access[rk][key]);
    };

    toggleColumn = (key) => {
        const next = this.columnAllChecked(key) ? 0 : 1;
        this.setState((prev) => {
            const access = { ...prev.access };
            this.allRowKeys().forEach((rk) => {
                const current = access[rk] || { is_view: 0, is_edit: 0, is_delete: 0 };
                const updated = { ...current, [key]: next };
                // Keep the View-prerequisite rule consistent for column-wide toggles.
                if ((key === "is_edit" || key === "is_delete") && next) {
                    updated.is_view = 1;
                }
                if (key === "is_view" && !next) {
                    updated.is_edit = 0;
                    updated.is_delete = 0;
                }
                access[rk] = updated;
            });
            return { access, savedMsg: "" };
        });
    };

    // Row-select checkbox: a row is "selected" when all three permissions are on.
    rowFullChecked = (rowKey) => {
        const a = this.state.access[rowKey];
        return !!(a && a.is_view && a.is_edit && a.is_delete);
    };

    // Toggle the whole row's access (View + Edit + Delete) at once.
    toggleRowFull = (rowKey) => {
        const next = this.rowFullChecked(rowKey) ? 0 : 1;
        this.setState((prev) => ({
            access: { ...prev.access, [rowKey]: { is_view: next, is_edit: next, is_delete: next } },
            savedMsg: "",
        }));
    };

    // Header select-all: every row across the grid is fully selected.
    allRowsFullChecked = () => {
        const keys = this.allRowKeys();
        return keys.length > 0 && keys.every((rk) => this.rowFullChecked(rk));
    };

    // Select / deselect every row in one click.
    toggleAllRowsFull = () => {
        const next = this.allRowsFullChecked() ? 0 : 1;
        this.setState((prev) => {
            const access = { ...prev.access };
            this.allRowKeys().forEach((rk) => {
                access[rk] = { is_view: next, is_edit: next, is_delete: next };
            });
            return { access, savedMsg: "" };
        });
    };

    handleSave = async () => {
        if (!this.state.selectedRole) {
            this.setState({ errorMsg: "Please select a role first." });
            return;
        }

        const institute_id = parseInt(Cookies.get("institutionid"));
        // one object per (sub-)module row; employee_role_id stays a string (varchar).
        // access is role-wide (not branch-scoped).
        const params = this.getRows().map((r) => {
            const a = this.state.access[r.key] || { is_view: 0, is_edit: 0, is_delete: 0 };
            return {
                module_id: parseInt(r.module_id),
                is_view: a.is_view ? 1 : 0,
                is_edit: a.is_edit ? 1 : 0,
                is_delete: a.is_delete ? 1 : 0,
                employee_role_id: this.state.selectedRole,
                institute_id,
            };
        });

        this.setState({ saving: true, errorMsg: "", savedMsg: "" });
        try {
            const { data } = await this.props.client.mutate({
                mutation: SAVE_ROLE_MODULE_ACCESS,
                variables: { params },
            });
            if (data && data.saveRoleModuleAccess) {
                this.setState({ saving: false, savedMsg: "Access saved successfully", showSuccessPopup: true });
                // auto-dismiss the success popup after a few seconds
                if (this._popupTimer) clearTimeout(this._popupTimer);
                this._popupTimer = setTimeout(() => this.setState({ showSuccessPopup: false }), 3000);
                // re-fetch from server so the grid reflects the saved state
                this.loadAccess(this.state.selectedRole);
            } else {
                this.setState({ saving: false, errorMsg: "Could not save access. Please try again." });
            }
        } catch (error) {
            this.setState({
                saving: false,
                errorMsg: (error.graphQLErrors && error.graphQLErrors.map(x => x.message).join(", ")) || "Something went wrong.",
            });
        }
    };

    render() {
        const rows = this.getRows();
        const visibleRows = this.getVisibleRows();
        // fields disabled for a mandatory role OR when the user has no edit access to
        // this module (is_edit = 0) → they can view the grid but not change/save it.
        const readOnly = this.isMandatoryRole() || !canEdit(EMPLOYEE_RESTRICTIONS_MODULE, EMPLOYEE_RESTRICTIONS_SUB);

        // How many rows each module has BEFORE the search. A module that really
        // has sub-modules must keep its header + sub-module labels even when the
        // search narrows it to a single matching row.
        const totalByModule = {};
        rows.forEach((r) => {
            totalByModule[r.module_name] = (totalByModule[r.module_name] || 0) + 1;
        });

        // Group ALL rows of the same module under one header (global grouping by
        // module_name), regardless of sort order — so a module whose rows aren't
        // contiguous still shows as a single section. The group keeps its first-
        // seen position and takes the first row's display_name as the header.
        const groups = [];
        const groupByModule = {};
        visibleRows.forEach((r) => {
            let g = groupByModule[r.module_name];
            if (!g) {
                g = { module_name: r.module_name, display_name: r.display_name || r.module_name, items: [] };
                groupByModule[r.module_name] = g;
                groups.push(g);
            }
            g.items.push(r);
        });

        // Render standalone (single-row) modules first, then the grouped sections
        // (multi-row) — stable within each — so a single module never appears
        // sandwiched in the middle of a section's sub-modules. Ordering follows the
        // unfiltered shape, so rows don't jump around as you type.
        const isSection = (g) => (totalByModule[g.module_name] || 0) > 1;
        groups.sort((a, b) => (isSection(a) ? 1 : 0) - (isSection(b) ? 1 : 0));

        const FIN = {
            card: { background: "#fff", borderRadius: 16, boxShadow: "0 4px 24px rgba(0,0,0,0.05)", overflow: "visible", border: "1px solid #e2e8f0" },
            cardHeader: { background: "#ffffff", padding: "20px 24px", display: "flex", alignItems: "center", gap: 14, borderBottom: "1px solid #f1f5f9", flexWrap: "wrap" },
            iconWrapper: { width: 38, height: 38, borderRadius: 10, background: "#f5f3ff", border: "1px solid #ddd6fe", display: "flex", alignItems: "center", justifyContent: "center", color: "#7c3aed", fontSize: 16, flexShrink: 0 },
            cardTitleText: { display: "flex", flexDirection: "column", textAlign: "left" },
            cardTitle: { color: "#0f172a", fontWeight: 700, fontSize: 16, margin: 0, letterSpacing: "-0.01em" },
            cardSubtitle: { color: "#64748b", fontSize: 12, fontWeight: 500, marginTop: 2 },
            headerActions: { display: "flex", gap: 10, marginLeft: "auto", flexWrap: "wrap", alignItems: "center" },
            label: { fontSize: 12, fontWeight: 600, color: "#6b7280", marginBottom: 4, display: "block" },
            select: { borderRadius: 9, border: "1.5px solid #e5e7eb", padding: "9px 12px", fontSize: 13, color: "#1f2937", background: "#f9fafb", minWidth: 220, height: 42 },
            searchWrap: { position: "relative", display: "flex", alignItems: "center" },
            searchIcon: { position: "absolute", left: 12, color: "#94a3b8", fontSize: 13, pointerEvents: "none" },
            search: { borderRadius: 9, border: "1.5px solid #e5e7eb", padding: "9px 32px 9px 32px", fontSize: 13, color: "#1f2937", background: "#f9fafb", minWidth: 240, height: 42 },
            searchClear: { position: "absolute", right: 10, background: "none", border: "none", padding: 0, lineHeight: 1, color: "#94a3b8", fontSize: 13, cursor: "pointer" },
            btnPrimary: { display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7, background: "#7c3aed", color: "#fff", border: "none", borderRadius: 9, padding: "10px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", height: 42 },
            content: { padding: "20px 24px" },
            empty: { padding: "40px 24px", textAlign: "center", color: "#94a3b8", fontSize: 13 },
            th: { padding: "12px 14px", fontSize: 12, fontWeight: 700, color: "#475569", borderBottom: "2px solid #e2e8f0", textAlign: "left", background: "#f8fafc" },
            thCenter: { padding: "12px 14px", fontSize: 12, fontWeight: 700, color: "#475569", borderBottom: "2px solid #e2e8f0", textAlign: "center", background: "#f8fafc" },
            td: { padding: "10px 14px", fontSize: 13, color: "#334155", borderBottom: "1px solid #f1f5f9" },
            tdCenter: { padding: "10px 14px", borderBottom: "1px solid #f1f5f9", textAlign: "center" },
        };

        return (
            <div style={FIN.card}>
                {/* Success popup — appears at the top of the screen after saving */}
                {this.state.showSuccessPopup ? (
                    <div style={{ position: "fixed", top: 24, left: "50%", transform: "translateX(-50%)", zIndex: 9999, background: "#059669", color: "#fff", padding: "14px 22px", borderRadius: 12, boxShadow: "0 8px 30px rgba(0,0,0,0.18)", display: "flex", alignItems: "center", gap: 10, fontSize: 14, fontWeight: 600 }}>
                        <i className="fas fa-check-circle" style={{ fontSize: 18 }} />
                        <span>{this.state.savedMsg || "Access saved successfully"}</span>
                        <i className="fal fa-times" style={{ marginLeft: 8, cursor: "pointer", opacity: 0.85 }} onClick={() => this.setState({ showSuccessPopup: false })} />
                    </div>
                ) : null}
                <div style={FIN.cardHeader}>
                    <div style={FIN.iconWrapper}><i className="fal fa-sliders-h" /></div>
                    <div style={FIN.cardTitleText}>
                        <h5 style={FIN.cardTitle}>Module-wise Access</h5>
                        <span style={FIN.cardSubtitle}>Set View / Edit / Delete access per module for a role</span>
                    </div>
                    <div style={FIN.headerActions}>
                        <div style={FIN.searchWrap}>
                            <i className="fal fa-search" style={FIN.searchIcon} />
                            <input
                                type="text"
                                value={this.state.search}
                                onChange={(e) => this.setState({ search: e.target.value })}
                                placeholder="Search modules..."
                                style={FIN.search}
                            />
                            {this.state.search ? (
                                <button
                                    type="button"
                                    onClick={() => this.setState({ search: "" })}
                                    style={FIN.searchClear}
                                    aria-label="Clear search"
                                >
                                    <i className="fal fa-times" />
                                </button>
                            ) : null}
                        </div>
                        <div>
                            <select style={FIN.select} value={this.state.selectedRole} onChange={this.handleRoleChange}>
                                <option value="">-- Select Role --</option>
                                {this.state.roles.map((r) => (
                                    <option key={r.id} value={r.id}>{r.role_name}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                <div style={FIN.content}>
                    {(!this.state.selectedRole) ? (
                        <div style={FIN.empty}>
                            <i className="fal fa-hand-pointer" style={{ fontSize: 30, display: "block", marginBottom: 10, color: "#cbd5e1" }} />
                            Select a role to configure module access.
                        </div>
                    ) : this.state.loadingAccess ? (
                        <TableShimmer cols={6} rows={6} widths={[40, 300, 90, 90, 90, 100]} />
                    ) : rows.length === 0 ? (
                        <div style={FIN.empty}>No modules available.</div>
                    ) : visibleRows.length === 0 ? (
                        <div style={FIN.empty}>
                            <i className="fal fa-search" style={{ fontSize: 30, display: "block", marginBottom: 10, color: "#cbd5e1" }} />
                            No modules match “{this.state.search.trim()}”.
                        </div>
                    ) : (
                        <>
                            {/* success is shown as a top popup; only errors stay inline */}
                            {this.state.errorMsg ? (
                                <p style={{ marginBottom: 12, fontWeight: 600, color: "#dc2626" }}>
                                    {this.state.errorMsg}
                                </p>
                            ) : null}
                            <div className="table-responsive">
                                <table className="table mb-0" style={{ borderCollapse: "collapse", width: "100%" }}>
                                    <thead>
                                        <tr>
                                            <th style={{ ...FIN.th, width: 60 }}>#</th>
                                            <th style={FIN.th}>Module</th>
                                            {PERMS.map((p) => (
                                                <th key={p.key} style={FIN.thCenter}>
                                                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                                                        <span>{p.label}</span>
                                                        <label style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 500, color: "#7c3aed", cursor: readOnly ? "not-allowed" : "pointer", margin: 0 }}>
                                                            <input type="checkbox" checked={this.columnAllChecked(p.key)} onChange={() => this.toggleColumn(p.key)} disabled={readOnly} style={{ accentColor: readOnly ? "#94a3b8" : "#7c3aed" }} />
                                                            All
                                                        </label>
                                                    </div>
                                                </th>
                                            ))}
                                            <th style={{ ...FIN.thCenter, borderLeft: "1px solid #e2e8f0" }}>
                                                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                                                    <span style={{ color: "#059669" }}>Full Access</span>
                                                    <label style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 500, color: "#059669", cursor: readOnly ? "not-allowed" : "pointer", margin: 0 }}>
                                                        <input type="checkbox" checked={this.allRowsFullChecked()} onChange={this.toggleAllRowsFull} disabled={readOnly} style={{ accentColor: readOnly ? "#94a3b8" : "#059669" }} />
                                                        All
                                                    </label>
                                                </div>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(() => {
                                            // Running row number across all groups.
                                            let rowNo = 0;
                                            // "Full Access" column — one click grants/revokes View+Edit+Delete for that module.
                                            const selectCell = (r) => (
                                                <td style={{ ...FIN.tdCenter, borderLeft: "1px solid #f1f5f9" }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={this.rowFullChecked(r.key)}
                                                        onChange={() => this.toggleRowFull(r.key)}
                                                        disabled={readOnly}
                                                        title="Select all actions for this module"
                                                        style={{ accentColor: readOnly ? "#94a3b8" : "#059669", width: 16, height: 16, cursor: readOnly ? "not-allowed" : "pointer" }}
                                                    />
                                                </td>
                                            );
                                            const checkboxes = (r) => {
                                                const a = this.state.access[r.key] || { is_view: 0, is_edit: 0, is_delete: 0 };
                                                return PERMS.map((p) => (
                                                    <td key={p.key} style={FIN.tdCenter}>
                                                        <input
                                                            type="checkbox"
                                                            checked={!!a[p.key]}
                                                            onChange={() => this.toggle(r.key, p.key)}
                                                            disabled={readOnly}
                                                            style={{ accentColor: readOnly ? "#94a3b8" : "#7c3aed", width: 16, height: 16, cursor: readOnly ? "not-allowed" : "pointer" }}
                                                        />
                                                    </td>
                                                ));
                                            };

                                            return groups.map((g, gIdx) => {
                                                // A main module has sub-modules only when it spans more than one
                                                // row (same `module` value repeated). A single row = standalone.
                                                // Judged on the unfiltered count, so a search that matches one
                                                // sub-module still shows it under its module header (and by its
                                                // own name) rather than as a standalone module row.
                                                const hasSubModules = isSection(g);

                                                // No sub-modules — render the standalone module as a flat row.
                                                if (!hasSubModules) {
                                                    return g.items.map((r) => {
                                                        rowNo += 1;
                                                        return (
                                                            <tr key={r.key}>
                                                                <td style={FIN.td}>{rowNo}</td>
                                                                <td style={{ ...FIN.td, fontWeight: 600, color: "#334155" }}>
                                                                    <i className="fal fa-cube" style={{ marginRight: 8, color: "#7c3aed" }} />
                                                                    {g.display_name || g.module_name}
                                                                </td>
                                                                {checkboxes(r)}
                                                                {selectCell(r)}
                                                            </tr>
                                                        );
                                                    });
                                                }

                                                // Has sub-modules — group header + indented sub-module rows.
                                                return (
                                                    <React.Fragment key={gIdx}>
                                                        <tr style={{ background: "#f5f3ff" }}>
                                                            <td colSpan={3 + PERMS.length} style={{ padding: "10px 14px", fontSize: 13, fontWeight: 700, color: "#5b21b6", borderBottom: "1px solid #ede9fe" }}>
                                                                <i className="fal fa-cube" style={{ marginRight: 8, color: "#7c3aed" }} />
                                                                {g.module_name}
                                                            </td>
                                                        </tr>
                                                        {/* Skip the empty-sub_module row inside a module that already
                                                            has real sub-pages — it carried no sub_module data and only
                                                            showed a blank/"Main" placeholder, so drop it from the grid. */}
                                                        {g.items.filter((r) => r.sub_module_name).map((r) => {
                                                            rowNo += 1;
                                                            return (
                                                                <tr key={r.key}>
                                                                    <td style={FIN.td}>{rowNo}</td>
                                                                    <td style={{ ...FIN.td, paddingLeft: 30, color: "#475569" }}>
                                                                        <i className="fal fa-level-up-alt fa-rotate-90" style={{ color: "#cbd5e1", marginRight: 8 }} />
                                                                        {r.display_name || r.sub_module_name || "--"}
                                                                    </td>
                                                                    {checkboxes(r)}
                                                                    {selectCell(r)}
                                                                </tr>
                                                            );
                                                        })}
                                                    </React.Fragment>
                                                );
                                            });
                                        })()}
                                    </tbody>
                                </table>
                            </div>

                            {/* Save action at the bottom */}
                            <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 14, marginTop: 20, paddingTop: 16, borderTop: "1px solid #f1f5f9" }}>
                                <button style={{ ...FIN.btnPrimary, opacity: readOnly ? 0.5 : 1, cursor: readOnly ? "not-allowed" : "pointer" }} onClick={this.handleSave} disabled={readOnly || !this.state.selectedRole || this.state.saving}>
                                    {this.state.saving
                                        ? <><span className="spinner-border spinner-border-sm" /> Saving...</>
                                        : <><i className="fas fa-save" /> Save Access</>}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    }
}

export default withApollo(ModuleAccessTab);
